// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TextField, { TextFieldPresentationProps, TextFieldProps } from "@awc/MobiusOverrides/TextField";
import { mergeWithCss, xMask } from "@awc/XDL/Utilities";
import xDefaultRules, { maxQtyNumber } from "@awc/XDL/Utilities/xValidate/xDefaultRules";
import IsNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { Typography } from "@insite/mobius/index";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect, useMemo, useState } from "react";

interface OwnProps extends Omit<TextFieldProps, "css"> {
    onDecreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    onIncreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    extendedStyles?: QtyFieldStyles;
    allowZero?: boolean;
    editable?: boolean;
    showLabel?: boolean;
    min?: number | string;
    max?: number | string;
    validationRules?: string;
    minimumOrderQty?: number;
    multipleSaleQty?: number;
    onChange?: any;
    labelOnLeft?: boolean;
}

type Props = OwnProps;

export interface QtyFieldStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    wrapper?: InjectableCss;
    actionClickable?: ClickablePresentationProps;
    countIcon?: IconPresentationProps;
    qtyLabel?: TypographyProps;
    valueText?: TypographyProps;
}

export const qtyFieldStyles: QtyFieldStyles = {};

const enterKey = 13;

const QtyField: FC<Props> = ({
    extendedStyles,
    onIncreaseChangeHandler,
    onDecreaseChangeHandler,
    disabled: disabledProps = false,
    value: currentValue,
    onChange: onChangeTextField,
    onBlur: onBlurTextField,
    onClick: onClickTextField,
    onKeyDown: onKeyDownTextField,
    label: labelOverride,
    allowZero = false,
    editable = true,
    showLabel = true,
    min = 0,
    max = maxQtyNumber,
    validationRules,
    minimumOrderQty,
    multipleSaleQty,
    labelOnLeft = false,
    ...qtyFieldsProps
}) => {
    const styles = useMemo(() => mergeWithCss(qtyFieldStyles, extendedStyles), []);
    const [callOnSubmitSafety, setCallOnSubmitSafety] = useState(false);

    let minQty = 0;
    let maxQty = maxQtyNumber;
    (validationRules || xDefaultRules["qty"]).split("|").forEach(item => {
        const [attr, val] = item.split(":");
        if (attr === "min") {
            minQty = Math.max(Number(min), +val);
        }
        if (attr === "max") {
            maxQty = Math.min(Number(max), +val);
        }
    }, {});

    const disabled = !callOnSubmitSafety && disabledProps;

    /**
     * UseEffect to wait until data change in cartQuantity state
     * then we do submit
     * src/FrontEnd/modules/client-framework/src/Common/Hooks/useAccessibleSubmit.ts
     */
    useEffect(() => {
        if (callOnSubmitSafety) {
            onBlurHandler();
            setCallOnSubmitSafety(false);
        }
    }, [currentValue]);

    const onClickHandler = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        event.currentTarget.focus();
        onClickTextField?.(event);
    };

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, force = false) => {
        event.target.value = xMask.qty(event.target.value);
        onChangeTextField?.(event, { force });
    };

    useEffect(() => {
        if (minQty > (currentValue as number) && !allowZero) {
            onChangeHandler?.(
                {
                    target: { value: minQty.toString() },
                    currentTarget: { value: minQty.toString() },
                } as any,
                true,
            );
        }
    }, [minQty, maxQty]);

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKey && !onKeyDownTextField) {
            event.stopPropagation();
            onBlurTextField?.(event as any);
        }
    };

    const onBlurHandler = (event?: React.ChangeEvent<HTMLInputElement>) => {
        if (event?.currentTarget) {
            event.currentTarget.value = xMask.qty(event.currentTarget.value);
            onChangeHandler(event);
        }
        onBlurTextField?.(event as any);
    };

    const onDecreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        let nextValue;
        // do rounding in case we have minimumOrderedQuantity and MultipleSaleQty
        if (multipleSaleQty && multipleSaleQty > 1) {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) - multipleSaleQty >= minQty
                    ? Number(currentValue) - multipleSaleQty
                    : multipleSaleQty;
        } else {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) - 1 >= minQty
                    ? Number(currentValue) - 1
                    : undefined;
        }

        if (allowZero && nextValue === undefined) {
            return;
            // eslint-disable-next-line no-else-return
        } else if (!allowZero && !nextValue) {
            return;
        }

        event.currentTarget.value = xMask.qty(`${nextValue}`);
        event.target.value = xMask.qty(`${nextValue}`);

        if (onDecreaseChangeHandler) {
            event = onDecreaseChangeHandler(event);
        }

        setCallOnSubmitSafety(true);
        onChangeHandler(event, true);
    };

    const onIncreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        let nextValue;
        // do rounding in case we have minimumOrderedQuantity and MultipleSaleQty
        if (multipleSaleQty && multipleSaleQty > 1) {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) + multipleSaleQty <= maxQty
                    ? Number(currentValue) + multipleSaleQty
                    : undefined;
        } else {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) + 1 <= maxQty
                    ? Number(currentValue) + 1
                    : undefined;
        }

        if (!nextValue) {
            return;
        }

        event.currentTarget.value = xMask.qty(`${nextValue}`);
        event.target.value = xMask.qty(`${nextValue}`);

        if (onIncreaseChangeHandler) {
            event = onIncreaseChangeHandler(event);
        }
        setCallOnSubmitSafety(true);
        onChangeHandler(event, true);
    };

    return (
        <>
            {editable ? (
                <>
                    {showLabel && (
                        <Typography {...styles.qtyLabel} {...{ showLabel }} {...{ labelOnLeft }}>
                            {labelOverride || translate("QTY")}
                        </Typography>
                    )}
                    <StyledWrapper
                        {...styles.wrapper}
                        data-test-selector="quantity-field"
                        {...{ labelOnLeft }}
                        className="qty-field-wrapper"
                    >
                        <TextField
                            type="qty"
                            disabled={disabled}
                            value={currentValue}
                            onChange={e => onChangeHandler(e)}
                            onBlur={onBlurHandler}
                            onClick={onClickHandler}
                            onKeyDown={onKeyDownHandler}
                            label={labelOverride || translate("QTY")}
                            {...styles.editableQuantityTextField}
                            {...qtyFieldsProps}
                            validationRules={validationRules}
                        />
                    </StyledWrapper>
                </>
            ) : (
                <>
                    <VisuallyHidden as="label">{labelOverride || translate("QTY")}</VisuallyHidden>
                    {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                    <Typography {...styles.valueText}>{currentValue}</Typography>
                </>
            )}
        </>
    );
};

export default QtyField;
